import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always on
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already consented
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (!savedConsent) {
      // Small delay to avoid layout shift on page load
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences({
          essential: true,
          analytics: parsed.analytics || false,
          marketing: parsed.marketing || false
        });
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (newPreferences) => {
    // Save to localStorage in the format expected by Analytics.js
    const consent = {
      essential: true,
      analytics: newPreferences.analytics,
      marketing: newPreferences.marketing,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowPreferences(false);

    // Dispatch event for Analytics component to pick up
    window.dispatchEvent(new CustomEvent('cookie_consent_updated', { 
      detail: consent 
    }));
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  const savePreferencesHandler = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-6xl mx-auto">
          {!showPreferences ? (
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-6 h-6 text-[#E91E8C] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-900 font-medium mb-1">We use cookies</p>
                  <p className="text-gray-600 text-sm">
                    We use cookies to improve your experience and analyse site usage. 
                    See our <a href="/cookie-policy" className="text-[#E91E8C] hover:underline">Cookie Policy</a> for more information.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
                  data-testid="cookie-preferences-btn"
                >
                  <Settings className="w-4 h-4" />
                  Preferences
                </button>
                <button
                  onClick={acceptEssential}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  data-testid="cookie-essential-btn"
                >
                  Essential Only
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white rounded-lg text-sm font-medium"
                  data-testid="cookie-accept-all-btn"
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                {/* Essential */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Essential Cookies</p>
                    <p className="text-sm text-gray-500">Required for the website to function properly</p>
                  </div>
                  <div className="px-3 py-1 bg-gray-200 text-gray-600 rounded text-sm">
                    Always On
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Analytics Cookies</p>
                    <p className="text-sm text-gray-500">Help us understand how visitors use our site</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#E91E8C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#E91E8C] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Marketing Cookies</p>
                    <p className="text-sm text-gray-500">Used to deliver relevant advertisements</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="sr-only peer"
                      data-testid="cookie-marketing-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#E91E8C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#E91E8C] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={savePreferencesHandler}
                  className="px-4 py-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white rounded-lg text-sm font-medium"
                  data-testid="cookie-save-preferences-btn"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
